/**
 * MMOMonetizationManager — Client-side manager for ship purchases and monetization.
 *
 * Handles REST API calls for purchasing ships, fetching prices,
 * purchase history, and refund eligibility. Listens for WebSocket
 * purchase confirmation/failure events.
 */

import { EventBus } from '../utils/EventBus';
import { MMOEvents } from './MMONetworkManager';
import { getApiBaseUrl } from '../utils/ApiConfig';
import { ServiceLocator, Services } from '../utils/ServiceLocator';
import type { WalletManager } from './WalletManager';
import type { Chain, Stablecoin, Purchase, ComponentDefinition, ComponentInstance, Crew, CrewSlot, CrewBonuses, RewardPool, GoldenAsteroidPayout, Station, StationType, StationAsteroid, StationInsurance, MineralType, PlayerMineral, AuctionListing, AuctionTransaction, MarketSummary, UnitWreck, TowOption, Refund, AllianceHQ, AllianceHQTier, AllianceLPDistribution, LPDistributionMode } from '../shared/common/MonetizationTypes';

const API_URL = getApiBaseUrl();

/** Shape of a single entry in the /api/purchase/prices `variants` array. */
interface ShipVariantApiResponse {
  id: string;
  shipClass: string;
  name: string;
  description?: string;
  priceUsd: number;
  hp: number;
  shields?: number;
  attack: number;
  defense: number;
  speed: number;
  attackRange: number;
  componentSlots: number;
  tonnage: number;
  cargoCapacity?: number;
  crewCapacity?: number;
  visionRange?: number;
  towCapacity?: number;
}

export interface ShipPriceInfo {
  unitType: string;
  priceUsd: number;
  hp: number;
  attack: number;
  defense: number;
  speed: number;
  attackRange: number;
  componentSlots: number;
  tonnage: number;
  // Variant-specific fields (populated when server has ship_variants table)
  variantId?: string;
  displayName?: string;
  shields?: number;
  visionRange?: number;
  cargoCapacity?: number;
  crewCapacity?: number;
  towCapacity?: number;
  description?: string;
}

export interface ShipVariantInfo {
  id: string;
  shipClass: string;
  name: string;
  priceUsd: number;
  hp: number;
  shields: number;
  attack: number;
  defense: number;
  speed: number;
  attackRange: number;
  componentSlots: number;
  towCapacity: number;
  tonnage: number;
  cargoCapacity: number;
  crewCapacity: number;
  visionRange: number;
  specialTrait?: string;
}

export interface PurchaseResult {
  success: boolean;
  purchaseId?: string;
  unitId?: string;
  error?: string;
}

export interface RefundEligibility {
  eligible: boolean;
  reason?: string;
  purchaseId?: string;
  totalInvested?: number;
  cooldownEndsAt?: string;
}

export interface ShipRepairQuote {
  success: boolean;
  unitId: string;
  unitType: string;
  hpNeeded: number;
  ironRequired: number;
  ironAvailable: number;
  missingIron: number;
  auctionAvailable: number;
  auctionPrice: number | null;
  totalIsdCost: number;
  marketplaceFee: number;
  grandTotal: number;
  playerIsdBalance: number;
  canAfford: boolean;
  allMineralsAvailable: boolean;
  canUseAuctionPurchase: boolean;
  dockedAt: string | null;
  error?: string;
}

export interface ShipRepairWithPurchaseResult {
  success: boolean;
  totalIsdSpent?: number;
  marketplaceFee?: number;
  repair?: any;
  error?: string;
}

export class MMOMonetizationManager {
  private prices: ShipPriceInfo[] = [];
  private purchaseHistory: Purchase[] = [];
  private getSessionToken: () => string | null;

  constructor(sessionTokenGetter: () => string | null) {
    this.getSessionToken = sessionTokenGetter;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    EventBus.on(MMOEvents.PURCHASE_CONFIRMED, this.onPurchaseConfirmed, this);
    EventBus.on(MMOEvents.PURCHASE_FAILED, this.onPurchaseFailed, this);
  }

  private onPurchaseConfirmed(payload: any): void {
    console.log('[Monetization] Purchase confirmed via WebSocket:', payload);
    EventBus.emit('monetization:purchase_confirmed', payload);
  }

  private onPurchaseFailed(payload: any): void {
    console.log('[Monetization] Purchase failed via WebSocket:', payload);
    EventBus.emit('monetization:purchase_failed', payload);
  }

  private authHeaders(): HeadersInit {
    const token = this.getSessionToken();
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Fetch all ship prices from the server. Caches the result.
   */
  async fetchShipPrices(): Promise<ShipPriceInfo[]> {
    try {
      const response = await fetch(`${API_URL}/purchase/prices`);
      if (!response.ok) {
        throw new Error(`Failed to fetch prices: ${response.status}`);
      }
      const data = await response.json();

      // Prefer variants (40 named ships) over base prices (10 generic classes)
      if (data.variants && data.variants.length > 0) {
        this.prices = data.variants.map((v: ShipVariantApiResponse): ShipPriceInfo => ({
          unitType: v.shipClass,
          variantId: v.id,
          displayName: v.name,
          description: v.description,
          priceUsd: v.priceUsd,
          hp: v.hp,
          shields: v.shields,
          attack: v.attack,
          defense: v.defense,
          speed: v.speed,
          attackRange: v.attackRange,
          componentSlots: v.componentSlots,
          tonnage: v.tonnage,
          cargoCapacity: v.cargoCapacity,
          crewCapacity: v.crewCapacity,
          visionRange: v.visionRange,
          towCapacity: v.towCapacity,
        }));
      } else {
        this.prices = data.prices || [];
      }

      return this.prices;
    } catch (error) {
      console.error('[Monetization] Failed to fetch ship prices:', error);
      return [];
    }
  }

  /**
   * Fetch all ship variants from the server.
   * Returns variant-level data (id, shipClass, name, full stats, specialTrait).
   */
  async fetchShipVariants(): Promise<ShipVariantInfo[]> {
    try {
      console.log('[Monetization] Fetching ship variants from', `${API_URL}/purchase/variants`);
      const response = await fetch(`${API_URL}/purchase/variants`);
      if (!response.ok) {
        throw new Error(`Failed to fetch variants: ${response.status}`);
      }
      const data = await response.json();
      const variants = data.variants || [];
      console.log(`[Monetization] Fetched ${variants.length} ship variants`);
      return variants;
    } catch (error) {
      console.error('[Monetization] Failed to fetch ship variants:', error);
      return [];
    }
  }

  /**
   * Get cached ship prices. Call fetchShipPrices() first to populate.
   */
  getShipPrices(): ShipPriceInfo[] {
    return this.prices;
  }

  /**
   * Purchase a ship via REST API.
   */
  async purchaseShip(
    unitType: string,
    chain: Chain,
    stablecoin: Stablecoin,
    worldId: number = 1,
    variantId?: string,
  ): Promise<PurchaseResult> {
    try {
      const body: Record<string, unknown> = { unitType, chain, stablecoin, worldId };
      if (variantId) body.variantId = variantId;
      const response = await fetch(`${API_URL}/purchase/ship`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      }

      // Refresh purchase history after successful purchase
      this.fetchPurchaseHistory();

      return {
        success: true,
        purchaseId: data.purchaseId,
        unitId: data.unitId,
      };
    } catch (error) {
      console.error('[Monetization] Purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  /**
   * Fetch purchase history for the authenticated user. Caches the result.
   */
  async fetchPurchaseHistory(): Promise<Purchase[]> {
    try {
      const response = await fetch(`${API_URL}/purchase/history`, {
        headers: this.authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.status}`);
      }

      const data = await response.json();
      this.purchaseHistory = data.purchases || [];
      return this.purchaseHistory;
    } catch (error) {
      console.error('[Monetization] Failed to fetch purchase history:', error);
      return [];
    }
  }

  /**
   * Get cached purchase history.
   */
  getPurchaseHistory(): Purchase[] {
    return this.purchaseHistory;
  }

  /**
   * Check if a ship is eligible for refund.
   */
  async checkRefundEligibility(shipId: string): Promise<RefundEligibility> {
    try {
      const response = await fetch(`${API_URL}/purchase/refund-eligibility/${shipId}`, {
        headers: this.authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to check eligibility: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[Monetization] Failed to check refund eligibility:', error);
      return { eligible: false, reason: 'Failed to check eligibility' };
    }
  }

  // ============ Component Marketplace ============

  /**
   * Fetch available components from the marketplace.
   */
  async fetchAvailableComponents(category?: string, shipType?: string): Promise<ComponentDefinition[]> {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (shipType) params.set('shipType', shipType);
      const qs = params.toString();
      const url = `${API_URL}/components/available${qs ? '?' + qs : ''}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch components: ${response.status}`);
      }
      const data = await response.json();
      return data.components || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch components:', error);
      return [];
    }
  }

  /**
   * Fetch the player's owned component instances.
   */
  async fetchPlayerComponents(): Promise<ComponentInstance[]> {
    try {
      const response = await fetch(`${API_URL}/components/inventory`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.components || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch player components:', error);
      return [];
    }
  }

  /**
   * Purchase a component and equip it to a ship.
   */
  /**
   * Get the player's ISD balance.
   */
  async getIsdBalance(): Promise<{ isdBalance: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/balance`, {
        headers: this.authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) return { isdBalance: 0, error: data.error };
      return { isdBalance: parseFloat(data.isdBalance) || 0 };
    } catch (error) {
      return { isdBalance: 0, error: 'Failed to fetch balance' };
    }
  }

  /**
   * Purchase a component with ISD credits (no wallet needed).
   */
  async purchaseComponentWithIsd(
    componentDefId: string,
    shipId: string,
  ): Promise<{ success: boolean; componentInstanceId?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/purchase`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ componentDefId, shipId, paymentMethod: 'isd' }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      return { success: true, componentInstanceId: data.componentInstanceId, purchaseId: data.purchaseId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  /**
   * Purchase a ship with ISD credits (no wallet needed).
   */
  async purchaseShipWithIsd(
    unitType: string,
    worldId: number,
    variantId?: string,
  ): Promise<{ success: boolean; purchaseId?: string; unitId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/purchase/ship`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ unitType, worldId, paymentMethod: 'isd', variantId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      return { success: true, purchaseId: data.purchaseId, unitId: data.unitId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  async purchaseComponent(
    componentDefId: string,
    shipId: string,
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; componentInstanceId?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/purchase`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ componentDefId, shipId, chain, stablecoin, paymentMethod: 'crypto' }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      }

      return {
        success: true,
        componentInstanceId: data.componentInstanceId,
        purchaseId: data.purchaseId,
      };
    } catch (error) {
      console.error('[Monetization] Component purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  /**
   * Upgrade an equipped component to the next tier.
   */
  async upgradeComponent(
    componentInstanceId: string,
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; newTier?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/upgrade`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ componentInstanceId, chain, stablecoin }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || `Upgrade failed: ${response.status}` };
      }

      return {
        success: true,
        newTier: data.newTier,
        purchaseId: data.purchaseId,
      };
    } catch (error) {
      console.error('[Monetization] Component upgrade failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Upgrade failed' };
    }
  }

  /**
   * Get player's equipped component inventory.
   */
  async fetchComponentInventory(): Promise<any[]> {
    try {
      const response = await fetch(`${API_URL}/components/inventory`, {
        headers: this.authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch inventory: ${response.status}`);
      }

      const data = await response.json();
      return data.components || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch component inventory:', error);
      return [];
    }
  }

  /**
   * Equip an owned component onto a ship.
   */
  async equipComponent(
    componentInstanceId: string,
    shipId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/equip`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ componentInstanceId, shipId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Equip failed' };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Component equip failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Equip failed' };
    }
  }

  /**
   * Unequip a component from a ship (moves to player inventory).
   */
  async unequipComponent(
    componentInstanceId: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/unequip`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ componentInstanceId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Unequip failed' };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Component unequip failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unequip failed' };
    }
  }

  /**
   * Repair a component's condition using minerals.
   */
  async repairComponent(
    componentInstanceId: string,
  ): Promise<{ success: boolean; newCondition?: number; mineralsSpent?: Record<string, number>; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/${componentInstanceId}/repair`, {
        method: 'POST',
        headers: this.authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Repair failed' };
      return { success: true, newCondition: data.newCondition, mineralsSpent: data.mineralsSpent };
    } catch (error) {
      console.error('[Monetization] Component repair failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Repair failed' };
    }
  }

  /**
   * Get repair cost estimate for a component.
   */
  async getRepairEstimate(
    componentInstanceId: string,
  ): Promise<{ success: boolean; costs?: Record<string, { needed: number; available: number }>; conditionPercent?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/components/${componentInstanceId}/repair-estimate`, {
        headers: this.authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Failed to get estimate' };
      return { success: true, costs: data.costs, conditionPercent: data.conditionPercent };
    } catch (error) {
      console.error('[Monetization] Repair estimate failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Estimate failed' };
    }
  }

  // ============ Crew System ============

  /**
   * Fetch player's crews and slots.
   */
  async fetchCrewList(): Promise<{ crews: Crew[]; slots: CrewSlot[] }> {
    try {
      const response = await fetch(`${API_URL}/crew/list`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed to fetch crews: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[Monetization] Failed to fetch crew list:', error);
      return { crews: [], slots: [] };
    }
  }

  /**
   * Recruit a new crew member (free, requires open slot).
   */
  async recruitCrew(name: string): Promise<{ success: boolean; crew?: Crew; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/recruit`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ name }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true, crew: data.crew };
    } catch (error) {
      console.error('[Monetization] Crew recruit failed:', error);
      return { success: false, error: 'Recruitment failed' };
    }
  }

  /**
   * Assign a crew to a ship.
   */
  async assignCrew(crewId: string, shipId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/assign`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ crewId, shipId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Crew assign failed:', error);
      return { success: false, error: 'Assignment failed' };
    }
  }

  /**
   * Unassign a crew from its ship (returns to barracks).
   */
  async unassignCrew(crewId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/unassign`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ crewId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Crew unassign failed:', error);
      return { success: false, error: 'Unassignment failed' };
    }
  }

  /**
   * Dismiss (permanently delete) a crew member.
   */
  async dismissCrew(crewId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/dismiss`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ crewId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Crew dismiss failed:', error);
      return { success: false, error: 'Dismissal failed' };
    }
  }

  /**
   * Purchase an additional crew slot ($10 USD).
   */
  async purchaseCrewSlot(
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; slotId?: string; slotNumber?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/buy-slot`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ chain, stablecoin }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true, slotId: data.slotId, slotNumber: data.slotNumber };
    } catch (error) {
      console.error('[Monetization] Crew slot purchase failed:', error);
      return { success: false, error: 'Slot purchase failed' };
    }
  }

  /**
   * Get crew bonuses for a specific ship.
   */
  async fetchCrewBonuses(shipId: string): Promise<{ hasCrew: boolean; crew?: Crew; bonuses?: CrewBonuses }> {
    try {
      const response = await fetch(`${API_URL}/crew/bonuses/${shipId}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[Monetization] Failed to fetch crew bonuses:', error);
      return { hasCrew: false };
    }
  }

  /**
   * Level up a crew member by paying credits.
   */
  async levelUpCrew(crewId: string): Promise<{ success: boolean; cost?: number; newLevel?: number; newXp?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/crew/level-up`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ crewId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return data;
    } catch (error) {
      console.error('[Monetization] Crew level-up failed:', error);
      return { success: false, error: 'Level-up failed' };
    }
  }

  // ============ Golden Asteroid Rewards ============

  /**
   * Fetch the current reward pool status.
   */
  async fetchRewardPool(): Promise<RewardPool | null> {
    try {
      const response = await fetch(`${API_URL}/purchase/reward-pool`);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[Monetization] Failed to fetch reward pool:', error);
      return null;
    }
  }

  /**
   * Fetch reward payout history for the authenticated user.
   */
  async fetchRewardHistory(): Promise<GoldenAsteroidPayout[]> {
    try {
      const response = await fetch(`${API_URL}/purchase/reward-history`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.payouts || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch reward history:', error);
      return [];
    }
  }

  // ============ Space Stations ============

  /**
   * Fetch station pricing.
   */
  async fetchStationPrices(): Promise<Record<string, number>> {
    try {
      const response = await fetch(`${API_URL}/stations/prices`);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      // API returns prices as array of {stationType, priceUsd, ...} - convert to Record<string, number>
      const pricesArray = data.prices || [];
      const pricesMap: Record<string, number> = {};
      for (const p of pricesArray) {
        pricesMap[p.stationType] = p.priceUsd;
      }
      return pricesMap;
    } catch (error) {
      console.error('[Monetization] Failed to fetch station prices:', error);
      return {};
    }
  }

  /**
   * Fetch player's owned stations.
   */
  async fetchStations(): Promise<Station[]> {
    try {
      const response = await fetch(`${API_URL}/stations/list`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.stations || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch stations:', error);
      return [];
    }
  }

  /**
   * Purchase a new station.
   */
  async purchaseStation(
    stationType: StationType,
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; stationId?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/stations/purchase`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationType, chain, stablecoin }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true, stationId: data.stationId, purchaseId: data.purchaseId };
    } catch (error) {
      console.error('[Monetization] Station purchase failed:', error);
      return { success: false, error: 'Purchase failed' };
    }
  }

  /**
   * Deploy a station from inventory to the map.
   */
  async deployStation(
    stationId: string,
    q: number,
    r: number,
    worldId: number = 1,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/stations/deploy`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId, worldId, q, r }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Station deploy failed:', error);
      return { success: false, error: 'Deploy failed' };
    }
  }

  /**
   * Relocate a deployed station to a new hex.
   */
  async relocateStation(
    stationId: string,
    q: number,
    r: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/stations/relocate`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId, q, r }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Station relocate failed:', error);
      return { success: false, error: 'Relocate failed' };
    }
  }

  /**
   * Relocate a deployed platform to a new hex.
   */
  async relocatePlatform(
    platformId: string,
    q: number,
    r: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/platforms/${platformId}/relocate`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ newQ: q, newR: r }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Platform relocate failed:', error);
      return { success: false, error: 'Relocate failed' };
    }
  }

  /**
   * Set a station's fee percentage.
   */
  async setStationFee(
    stationId: string,
    feePct: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/stations/set-fee`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId, feePct }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Station set-fee failed:', error);
      return { success: false, error: 'Set fee failed' };
    }
  }

  /**
   * Fetch asteroids stored at a station.
   */
  async fetchStationAsteroids(stationId: string): Promise<StationAsteroid[]> {
    try {
      const response = await fetch(`${API_URL}/stations/${stationId}/asteroids`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.asteroids || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch station asteroids:', error);
      return [];
    }
  }

  /**
   * Retrieve a stored asteroid from a station.
   */
  async retrieveAsteroid(
    stationId: string,
    depositId: string,
  ): Promise<{ success: boolean; asteroidId?: string; variant?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/stations/retrieve-asteroid`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId, depositId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error };
      return { success: true, asteroidId: data.asteroidId, variant: data.variant };
    } catch (error) {
      console.error('[Monetization] Asteroid retrieval failed:', error);
      return { success: false, error: 'Retrieval failed' };
    }
  }

  // ============ Mineral Economy ============

  /**
   * Fetch all mineral type definitions.
   */
  async fetchMineralTypes(): Promise<MineralType[]> {
    try {
      const response = await fetch(`${API_URL}/minerals/types`);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.types || data.mineralTypes || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch mineral types:', error);
      return [];
    }
  }

  /**
   * Fetch player's mineral inventory.
   */
  async fetchMineralInventory(): Promise<PlayerMineral[]> {
    try {
      const response = await fetch(`${API_URL}/minerals/inventory`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.minerals || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch mineral inventory:', error);
      return [];
    }
  }

  /**
   * Mine an asteroid for minerals.
   */
  async mineAsteroid(unitId: string, asteroidId: string, worldId: number = 1): Promise<{ success: boolean; minerals?: Record<string, number>; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/minerals/mine`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ unitId, asteroidId, worldId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Mining failed: ${response.status}` };
      return { success: true, minerals: data.minerals };
    } catch (error) {
      console.error('[Monetization] Mining failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Mining failed' };
    }
  }

  // ============ Per-Location Minerals ============

  /**
   * Fetch mineral inventory with per-location breakdown.
   * Returns { locations: [...], totals: {...} }
   */
  async fetchMineralInventoryBreakdown(): Promise<{
    locations: { locationType: string; locationId: string; locationName: string; minerals: { mineralTypeId: string; mineralName: string; displayName: string; amount: number; rarity: string; color: string }[] }[];
    totals: Record<string, number>;
  }> {
    try {
      const response = await fetch(`${API_URL}/minerals/inventory?breakdown=true`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return { locations: data.locations || [], totals: data.totals || {} };
    } catch (error) {
      console.error('[Monetization] Failed to fetch mineral breakdown:', error);
      return { locations: [], totals: {} };
    }
  }

  /**
   * Fetch minerals at a specific location (station or planet).
   */
  async fetchLocationMinerals(locationType: string, locationId: string): Promise<{ mineralTypeId: string; mineralName: string; displayName: string; amount: number; rarity: string; color: string }[]> {
    try {
      const response = await fetch(`${API_URL}/minerals/location/${locationType}/${locationId}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.minerals || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch location minerals:', error);
      return [];
    }
  }

  /**
   * Initiate a tick-based mineral transfer between two locations.
   */
  async initiateTransfer(
    fromLocationType: string, fromLocationId: string,
    toLocationType: string, toLocationId: string,
  ): Promise<{ success: boolean; batchId?: string; totalMinerals?: number; estimatedTicks?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/minerals/transfer-location`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ fromLocationType, fromLocationId, toLocationType, toLocationId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Transfer failed: ${response.status}` };
      return { success: true, batchId: data.batchId, totalMinerals: data.totalMinerals, estimatedTicks: data.estimatedTicks };
    } catch (error) {
      console.error('[Monetization] Transfer initiation failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Transfer failed' };
    }
  }

  /**
   * Fetch active and recent mineral transfers.
   */
  async fetchMineralTransfers(): Promise<any[]> {
    try {
      const response = await fetch(`${API_URL}/minerals/transfers`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.transfers || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch transfers:', error);
      return [];
    }
  }

  /**
   * Cancel an active mineral transfer.
   */
  async cancelMineralTransfer(batchId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/minerals/transfer-location/${batchId}/cancel`, {
        method: 'POST',
        headers: this.authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Cancel failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Transfer cancel failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Cancel failed' };
    }
  }

  /**
   * Load minerals from a station/planet into a docked ship's cargo.
   */
  async loadCargoFromLocation(
    unitId: string, locationType: string, locationId: string,
    mineralTypeId: string, amount: number,
  ): Promise<{ success: boolean; loaded?: number; cargoUsed?: number; cargoCapacity?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/minerals/load-cargo`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ unitId, locationType, locationId, mineralTypeId, amount }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Load failed: ${response.status}` };
      return { success: true, loaded: data.loaded, cargoUsed: data.cargoUsed, cargoCapacity: data.cargoCapacity };
    } catch (error) {
      console.error('[Monetization] Load cargo failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Load cargo failed' };
    }
  }

  // ============ Auction House ============

  /**
   * Fetch active auction listings with optional filters.
   */
  async fetchAuctionListings(params?: {
    mineralTypeId?: string;
    sort?: string;
    limit?: number;
    offset?: number;
  }): Promise<AuctionListing[]> {
    try {
      const qs = new URLSearchParams();
      if (params?.mineralTypeId) qs.set('mineral_type_id', params.mineralTypeId);
      if (params?.sort) qs.set('sort', params.sort);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const query = qs.toString();
      const url = `${API_URL}/auction/listings${query ? '?' + query : ''}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.listings || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch auction listings:', error);
      return [];
    }
  }

  /**
   * Create a new auction listing.
   */
  async createAuctionListing(
    mineralTypeId: string,
    quantity: number,
    pricePerUnit: number,
  ): Promise<{ success: boolean; listing?: AuctionListing; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/auction/list`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ mineralTypeId, quantity, pricePerUnit }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Create listing failed: ${response.status}` };
      return { success: true, listing: data.listing || data };
    } catch (error) {
      console.error('[Monetization] Create listing failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Create listing failed' };
    }
  }

  /**
   * Buy from an auction listing.
   */
  async buyAuctionListing(listingId: string, quantity: number): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/auction/buy`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ listingId, quantity }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Buy failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Buy listing failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Buy failed' };
    }
  }

  /**
   * Cancel an auction listing.
   */
  async cancelAuctionListing(listingId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/auction/cancel`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ listingId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Cancel failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Cancel listing failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Cancel failed' };
    }
  }

  /**
   * Fetch player's own auction listings.
   */
  async fetchMyAuctionListings(): Promise<AuctionListing[]> {
    try {
      const response = await fetch(`${API_URL}/auction/my-listings`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.listings || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch my listings:', error);
      return [];
    }
  }

  /**
   * Fetch player's auction transaction history.
   */
  async fetchAuctionTransactions(): Promise<AuctionTransaction[]> {
    try {
      const response = await fetch(`${API_URL}/auction/history`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.transactions || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch auction transactions:', error);
      return [];
    }
  }

  /**
   * Fetch market price summary for all minerals.
   */
  async fetchMarketSummary(): Promise<MarketSummary[]> {
    try {
      const response = await fetch(`${API_URL}/auction/summary`);
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.summary || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch market summary:', error);
      return [];
    }
  }

  // ============ Wreckage & Repair ============

  /**
   * Fetch player's wrecks.
   */
  async fetchWrecks(): Promise<UnitWreck[]> {
    try {
      const response = await fetch(`${API_URL}/wrecks/list`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.wrecks || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch wrecks:', error);
      return [];
    }
  }

  /**
   * Fetch tow options for a wreck.
   */
  async fetchTowOptions(wreckId: string): Promise<TowOption[]> {
    try {
      const response = await fetch(`${API_URL}/wrecks/${wreckId}/tow-options`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.options || data || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch tow options:', error);
      return [];
    }
  }

  /**
   * Request a tow for a wreck.
   */
  async requestTow(
    wreckId: string,
    destination: string,
    destinationId?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const body: any = { wreckId, destination };
      if (destinationId) body.stationId = destinationId;
      const response = await fetch(`${API_URL}/wrecks/request-tow`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Tow failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Tow request failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Tow failed' };
    }
  }

  /**
   * Start repairing a wreck.
   */
  async startRepair(wreckId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/wrecks/start-repair`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ wreckId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Repair failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Repair failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Repair failed' };
    }
  }

  async getShipRepairQuote(unitId: string): Promise<ShipRepairQuote> {
    try {
      const response = await fetch(`${API_URL}/repairs/ship-repair-quote`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ unitId }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || `Quote failed: ${response.status}` } as ShipRepairQuote;
      }
      return data as ShipRepairQuote;
    } catch (error) {
      console.error('[Monetization] Ship repair quote failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Quote failed' } as ShipRepairQuote;
    }
  }

  async repairShipWithPurchase(unitId: string): Promise<ShipRepairWithPurchaseResult> {
    try {
      const response = await fetch(`${API_URL}/repairs/ship-repair-with-purchase`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ unitId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Repair failed: ${response.status}` };
      return data as ShipRepairWithPurchaseResult;
    } catch (error) {
      console.error('[Monetization] Ship repair with purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Repair failed' };
    }
  }

  /**
   * Salvage a wreck for relics and bonus minerals.
   */
  async salvageWreck(wreckId: string): Promise<{ success: boolean; relic?: any; minerals?: Record<string, number>; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/wrecks/salvage`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ wreckId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Salvage failed: ${response.status}` };
      return { success: true, relic: data.relic, minerals: data.bonusMinerals };
    } catch (error) {
      console.error('[Monetization] Salvage failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Salvage failed' };
    }
  }

  /**
   * Claim an unclaimed wreck.
   * @param wreckId - The wreck to claim
   * @param unitId - Optional unit ID for proximity-based claims (non-owner)
   */
  async claimWreck(wreckId: string, unitId?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const body: Record<string, unknown> = { wreckId };
      if (unitId) body.unitId = unitId;
      const response = await fetch(`${API_URL}/wrecks/claim`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Claim failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Claim failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Claim failed' };
    }
  }

  // ============ Station Insurance ============

  /**
   * Get insurance status for a station, including current premium and cost breakdown.
   */
  async fetchInsuranceStatus(stationId: string): Promise<{
    insurance: StationInsurance | null;
    currentPremium: number | null;
    storedValue: number;
    baseRate: number;
    defenseMultiplier: number;
    effectiveRate: number;
  }> {
    try {
      const response = await fetch(`${API_URL}/insurance/status/${stationId}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return {
        insurance: data.insurance || null,
        currentPremium: data.currentPremium ?? null,
        storedValue: data.storedValue ?? 0,
        baseRate: data.baseRate ?? 3.0,
        defenseMultiplier: data.defenseMultiplier ?? 1.0,
        effectiveRate: data.effectiveRate ?? 3.0,
      };
    } catch (error) {
      console.error('[Monetization] Failed to fetch insurance status:', error);
      return { insurance: null, currentPremium: null, storedValue: 0, baseRate: 3.0, defenseMultiplier: 1.0, effectiveRate: 3.0 };
    }
  }

  /**
   * List all insurance policies for the authenticated user.
   */
  async fetchInsuranceList(): Promise<StationInsurance[]> {
    try {
      const response = await fetch(`${API_URL}/insurance/list`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.insurances || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch insurance list:', error);
      return [];
    }
  }

  /**
   * Purchase insurance for a station.
   */
  async purchaseInsurance(stationId: string): Promise<{ success: boolean; insuranceId?: string; premium?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/insurance/purchase`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      return { success: true, insuranceId: data.insuranceId, premium: data.premium };
    } catch (error) {
      console.error('[Monetization] Insurance purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Insurance purchase failed' };
    }
  }

  /**
   * Renew lapsed, expired, or claimed insurance for a station.
   */
  async renewInsurance(stationId: string): Promise<{ success: boolean; premium?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/insurance/renew`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ stationId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Renewal failed: ${response.status}` };
      return { success: true, premium: data.premium };
    } catch (error) {
      console.error('[Monetization] Insurance renewal failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Insurance renewal failed' };
    }
  }

  // ============ Refund System ============

  /**
   * Request a refund for a purchase.
   */
  async requestRefund(purchaseId: string): Promise<{ success: boolean; refundId?: string; queuePosition?: number; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/refund/request`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ purchaseId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Refund request failed: ${response.status}` };
      return { success: true, refundId: data.refundId, queuePosition: data.queuePosition };
    } catch (error) {
      console.error('[Monetization] Refund request failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Refund request failed' };
    }
  }

  /**
   * Get a refund estimate for a purchase.
   */
  async fetchRefundEstimate(purchaseId: string): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/refund/estimate/${purchaseId}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('[Monetization] Failed to fetch refund estimate:', error);
      return null;
    }
  }

  /**
   * Fetch refund history for the authenticated user.
   */
  async fetchRefundHistory(): Promise<Refund[]> {
    try {
      const response = await fetch(`${API_URL}/refund/history`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.refunds || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch refund history:', error);
      return [];
    }
  }

  /**
   * Fetch the refund queue status.
   */
  async fetchRefundQueue(): Promise<{ queue: any; userQueuedRefunds: Refund[] }> {
    try {
      const response = await fetch(`${API_URL}/refund/queue`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return { queue: data.queue, userQueuedRefunds: data.userQueuedRefunds || [] };
    } catch (error) {
      console.error('[Monetization] Failed to fetch refund queue:', error);
      return { queue: null, userQueuedRefunds: [] };
    }
  }

  /**
   * Cancel a pending/queued refund.
   */
  async cancelRefund(refundId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/refund/cancel`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ refundId }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Cancel failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Refund cancel failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Cancel failed' };
    }
  }

  // ============ Alliance Economy ============

  /**
   * Fetch alliance HQ details.
   */
  async fetchAllianceHQ(allianceId: number): Promise<AllianceHQ | null> {
    try {
      const response = await fetch(`${API_URL}/alliance-economy/hq/${allianceId}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.hq || null;
    } catch (error) {
      console.error('[Monetization] Failed to fetch alliance HQ:', error);
      return null;
    }
  }

  /**
   * Fetch alliance LP distributions.
   */
  async fetchAllianceDistributions(allianceId: number, limit?: number): Promise<AllianceLPDistribution[]> {
    try {
      const qs = limit ? `?limit=${limit}` : '';
      const response = await fetch(`${API_URL}/alliance-economy/distributions/${allianceId}${qs}`, {
        headers: this.authHeaders(),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      return data.distributions || [];
    } catch (error) {
      console.error('[Monetization] Failed to fetch alliance distributions:', error);
      return [];
    }
  }

  /**
   * Purchase an alliance HQ.
   */
  async purchaseAllianceHQ(
    allianceId: number,
    hqTier: AllianceHQTier,
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; hqId?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/alliance-economy/purchase-hq`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ allianceId, hqTier, chain, stablecoin }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      return { success: true, hqId: data.hqId, purchaseId: data.purchaseId };
    } catch (error) {
      console.error('[Monetization] Alliance HQ purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }

  /**
   * Upgrade alliance HQ to a higher tier.
   */
  async upgradeAllianceHQ(
    allianceId: number,
    newTier: AllianceHQTier,
    chain: Chain,
    stablecoin: Stablecoin,
  ): Promise<{ success: boolean; hqId?: string; purchaseId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/alliance-economy/upgrade-hq`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ allianceId, newTier, chain, stablecoin }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Upgrade failed: ${response.status}` };
      return { success: true, hqId: data.hqId, purchaseId: data.purchaseId };
    } catch (error) {
      console.error('[Monetization] Alliance HQ upgrade failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Upgrade failed' };
    }
  }

  /**
   * Configure LP share settings for the alliance.
   */
  async configureAllianceLPShare(
    allianceId: number,
    sharePct: number,
    distributionMode: LPDistributionMode,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/alliance-economy/configure-lp-share`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ allianceId, sharePct, distributionMode }),
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || `Configure failed: ${response.status}` };
      return { success: true };
    } catch (error) {
      console.error('[Monetization] Alliance LP share config failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Configure failed' };
    }
  }

  // ============ Wallet-Integrated Purchases ============

  /**
   * Get the WalletManager from the ServiceLocator.
   * Returns null if not registered (graceful degradation).
   */
  getWalletManager(): WalletManager | null {
    return ServiceLocator.tryGet<WalletManager>(Services.WALLET) ?? null;
  }

  /**
   * Purchase a ship using the connected Solana wallet.
   *
   * This is the wallet-integrated purchase flow. It checks for a connected
   * wallet, then sends the purchase request to the server with the wallet
   * address included. The server handles payment verification.
   *
   * Defaults to chain: 'solana' and stablecoin: 'USDC'.
   */
  async purchaseShipWithWallet(
    unitType: string,
    worldId: number = 1,
  ): Promise<PurchaseResult> {
    try {
      const wallet = this.getWalletManager();
      if (!wallet) {
        return { success: false, error: 'Wallet manager not available.' };
      }

      if (!wallet.isConnected()) {
        return { success: false, error: 'No wallet connected. Please connect your Solana wallet first.' };
      }

      const walletInfo = wallet.getWalletInfo();
      if (!walletInfo) {
        return { success: false, error: 'Unable to read wallet info.' };
      }

      console.log(`[Monetization] Purchasing ${unitType} with wallet ${walletInfo.address}`);

      const response = await fetch(`${API_URL}/purchase/ship`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          unitType,
          chain: 'solana' as Chain,
          stablecoin: 'USDC' as Stablecoin,
          worldId,
          walletAddress: walletInfo.address,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || `Purchase failed: ${response.status}` };
      }

      // Refresh purchase history after successful purchase
      this.fetchPurchaseHistory();

      return {
        success: true,
        purchaseId: data.purchaseId,
        unitId: data.unitId,
      };
    } catch (error) {
      console.error('[Monetization] Wallet purchase failed:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Wallet purchase failed' };
    }
  }

  /**
   * Clean up event listeners.
   */
  destroy(): void {
    EventBus.off(MMOEvents.PURCHASE_CONFIRMED, this.onPurchaseConfirmed, this);
    EventBus.off(MMOEvents.PURCHASE_FAILED, this.onPurchaseFailed, this);
  }
}
